import React, { ReactElement, memo, useRef, useCallback, useState, useEffect } from 'react';
import { useForm, FormProvider, useFormContext, FieldErrors } from 'react-hook-form';

import cls from 'classnames';

import formatDate from 'date-fns/format';
import noop from 'lodash/noop';

import { BusinessTravelCheckbox } from '@ott/business-travel-checkbox';
import Button from '@ott/button';
import FirstErrorShower, { prepareRHFErrorsDeeply } from '@ott/first-error-shower';
import ReverseIcon from '@ott/icon-reverse';
import { useLayoutContext } from '@ott/layout-context';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Datepicker from './blocks/Datepicker/Datepicker';
import GeoAutocomplete from './blocks/GeoAutocomplete/GeoAutocomplete';
import SearchHistory from './blocks/SearchHistory/SearchHistory';
import Travellers from './blocks/Travellers/Travellers';
import { FIELDS, FORM_NAME } from './constants';
import focusBySelector, { FIELD_SELECTORS } from './helpers/focusBySelector';
import prepareUrlParams, { UrlParams } from './helpers/prepareUrlParams';
import saveSearchHistory from './helpers/saveSearchHistory';
import useInitialValues from './hooks/useInitialValues';
import useSuggestions from './hooks/useSuggestions';
import { trackSubmitFailed, trackSwapClick } from './statistic/events';
import { SearchForm } from './types';

import styles from './BusSearchForm.scss';

const queryClient = new QueryClient();

export type RouterQueryObject = { query: Record<string, string> };
export type WindowLocationObject = { search: string };

export type BusSearchFormProps = {
  className?: string;
  location?: RouterQueryObject | WindowLocationObject;
  onSubmit: (query: UrlParams['query'], seoPath: UrlParams['seoPath']) => void;
  onSubmitFail?: () => void;
  /**
   * Запрещает подстановку значений из истории поиска
   */
  preventAutofillFromHistory?: boolean;
  showBusinessTripCheckbox?: boolean;
};

const BusSearchForm = ({
  className,
  location,
  onSubmit,
  onSubmitFail = noop,
  preventAutofillFromHistory,
  showBusinessTripCheckbox = false,
}: BusSearchFormProps): ReactElement => {
  const formRef = useRef<HTMLFormElement>(null);
  const departureRef = useRef<HTMLInputElement>(null);
  const destinationRef = useRef<HTMLInputElement>(null);

  const [isDepartureOpen, setIsDepartureOpen] = useState(false);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);

  const [submitTime, setSubmitTime] = useState<null | number>(null);

  const { isDesktop } = useLayoutContext();

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitted, isSubmitSuccessful, errors },
  } = useFormContext<SearchForm>();

  const {
    areInitialValuesLoading,
    shouldChooseDate,
    abortInitialDepartureSettingIfNeeded,
    abortInitialDestinationSettingIfNeeded,
  } = useInitialValues({
    location,
    preventAutofillFromHistory,
  });
  useEffect(() => {
    if (shouldChooseDate && !areInitialValuesLoading && isDesktop) {
      focusBySelector(FIELD_SELECTORS[FIELDS.DATE]);
    }
  }, [shouldChooseDate, areInitialValuesLoading, isDesktop]);

  const [departure, departureOption, destination, destinationOption] = watch([
    FIELDS.DEPARTURE,
    FIELDS.DEPARTURE_OPTION,
    FIELDS.DESTINATION,
    FIELDS.DESTINATION_OPTION,
  ]);

  const {
    departureSuggestions,
    destinationSuggestions,
    areDefaultDepartureSuggestionsLoading,
    areDefaultDestinationSuggestionsLoading,
    areDepartureSuggestionsLoading,
    areDestinationSuggestionsLoading,
    areDestinationSuggestionsByDepartureIdLoading,
  } = useSuggestions({
    isDepartureOpen,
    isDestinationOpen,
    departureInput: departure,
    destinationInput: destination,
    // departureId/destinationId могут быть как id станции, так и id гео поинта
    // departureGeoPointId - всегда id гео поинта. Нужен для кейса получения дефолтных гео поинтов прибытия
    // на основании выбранного гео поинта отправления
    departureId: departureOption?.value,
    destinationId: destinationOption?.value,
    departureGeoPointId: departureOption?.geoPoint.id,
  });

  const onFormSubmit = (submittedData: SearchForm) => {
    const preparedData = {
      ...submittedData,
      date: formatDate(new Date(submittedData.date), 'yyyy-MM-dd'),
    };

    saveSearchHistory(preparedData);

    const { query, seoPath } = prepareUrlParams(preparedData);
    onSubmit(query, seoPath);
  };

  const onFormSubmitFail = (params: FieldErrors) => {
    trackSubmitFailed({
      fieldsWithError: Object.keys(params),
      departureInput: departure,
      destinationInput: destination,
    });
    onSubmitFail();
  };

  const handleDepartureSelect = useCallback(() => {
    if (isDesktop) {
      destinationRef.current?.focus();
    }
  }, [destinationRef, isDesktop]);

  const handleDestinationSelect = useCallback(() => {
    if (isDesktop) {
      // После обновления реакта до 17 версии handleClickOutside в компонентах Travellers и Datepicker перестал корректно работать:
      // клик при выборе значения предыдущего поля инициализирует фокус на следующем поле и последующую установку хендлера handleClickOutside,
      // при этом сам клик успевает отловиться этим же хендлером, что вызывает закрытие оверлея у Travellers или Datepicker.
      // Поэтому приходится обернуть фокусировку в setTimeOut
      setTimeout(() => focusBySelector(FIELD_SELECTORS[FIELDS.DATE]));
    }
  }, [isDesktop]);

  const handleDateSelect = useCallback(() => {
    if (isDesktop) {
      setTimeout(() => focusBySelector(FIELD_SELECTORS[FIELDS.ADULTS]));
    }
  }, [isDesktop]);

  const handleDepartureOpen = useCallback(() => setIsDepartureOpen(true), []);
  const handleDepartureClose = useCallback(() => setIsDepartureOpen(false), []);
  const handleDestinationOpen = useCallback(() => setIsDestinationOpen(true), []);
  const handleDestinationClose = useCallback(() => setIsDestinationOpen(false), []);

  const handleSwapClick = () => {
    if (departureOption && destinationOption) {
      setValue(FIELDS.DEPARTURE, destination);
      setValue(FIELDS.DEPARTURE_OPTION, destinationOption);
      setValue(FIELDS.DESTINATION, departure);
      setValue(FIELDS.DESTINATION_OPTION, departureOption);
    }
    trackSwapClick({
      isChanged: Boolean(departureOption && destinationOption),
    });
  };

  const renderSwapButton = ({ isMobile }: { isMobile: boolean }) => {
    // Делаю сразу оба варианта свап кнопки для мобилки и десктопа, чтобы корректно работало с SSR
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className={cls(styles.swapButton, {
          [styles.isMobile]: isMobile,
          [styles.isDesktop]: !isMobile,
        })}
        onClick={handleSwapClick}
      >
        {/* @ts-ignore */}
        <ReverseIcon className={styles.swapIcon} />
      </div>
    );
  };

  const preparedErrors = prepareRHFErrorsDeeply(errors);

  return (
    <>
      <form
        data-locator="bus-search-form"
        id={FORM_NAME}
        onSubmit={handleSubmit(onFormSubmit, onFormSubmitFail)}
        ref={formRef}
        className={cls(styles.SearchForm, className)}
      >
        <FirstErrorShower
          container={formRef.current}
          errors={preparedErrors}
          show={!isSubmitSuccessful && isSubmitted}
          time={submitTime}
          useRHFErrors={true}
        />
        <GeoAutocomplete
          abortInitialValuesSettingIfNeeded={abortInitialDepartureSettingIfNeeded}
          areSuggestionsLoading={
            areInitialValuesLoading ||
            areDefaultDepartureSuggestionsLoading ||
            areDepartureSuggestionsLoading
          }
          inputRef={departureRef}
          isOpen={isDepartureOpen}
          onClose={handleDepartureClose}
          onOpen={handleDepartureOpen}
          onSelect={handleDepartureSelect}
          options={departureSuggestions}
          type={FIELDS.DEPARTURE}
          className={styles.fromAutocomplete}
        />
        {renderSwapButton({ isMobile: false })}
        <GeoAutocomplete
          abortInitialValuesSettingIfNeeded={abortInitialDestinationSettingIfNeeded}
          areSuggestionsLoading={
            areInitialValuesLoading ||
            areDefaultDestinationSuggestionsLoading ||
            areDestinationSuggestionsLoading ||
            areDestinationSuggestionsByDepartureIdLoading
          }
          inputRef={destinationRef}
          isOpen={isDestinationOpen}
          onClose={handleDestinationClose}
          onOpen={handleDestinationOpen}
          onSelect={handleDestinationSelect}
          options={destinationSuggestions}
          type={FIELDS.DESTINATION}
        />
        {renderSwapButton({ isMobile: true })}
        <Datepicker className={styles.datePicker} onDateSelect={handleDateSelect} />
        <Travellers />
        {!__WHITE_LABEL__ && showBusinessTripCheckbox && (
          <div className={styles.businessTravelCheckboxWrapMobile}>
            <BusinessTravelCheckbox vertical="bus" />
          </div>
        )}
        <Button
          onClick={() => setSubmitTime(Date.now)}
          specificLocators="submit-search-form"
          type="submit"
          className={styles.submitButton}
        >
          Найти
        </Button>
      </form>
      <div className={styles.formFooter}>
        <SearchHistory
          abortInitialDepartureSettingIfNeeded={abortInitialDepartureSettingIfNeeded}
          abortInitialDestinationSettingIfNeeded={abortInitialDestinationSettingIfNeeded}
          onClick={handleSubmit(onFormSubmit, onFormSubmitFail)}
        />
        {!__WHITE_LABEL__ && showBusinessTripCheckbox && (
          <div className={styles.businessTravelCheckboxWrap}>
            <BusinessTravelCheckbox vertical="bus" />
          </div>
        )}
      </div>
    </>
  );
};

/* eslint-disable react/jsx-props-no-spreading */
const BusSearchFormWithFormProvider = (props: BusSearchFormProps) => {
  const formMethods = useForm<SearchForm>();

  return (
    <FormProvider {...formMethods}>
      <BusSearchForm {...props} />
    </FormProvider>
  );
};

export const BusSearchFormWithReactQueryProvider = memo((props: BusSearchFormProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <BusSearchFormWithFormProvider {...props} />
    </QueryClientProvider>
  );
});

export default memo(BusSearchFormWithFormProvider);
